import SummaryApi from "../common";

let csrfToken = null;
let fetchingPromise = null;

const isJsonResponse = (response) =>
  (response.headers.get("content-type") || "").includes("application/json");

const isCsrfFailureResponse = async (response) => {
  if (response.status !== 403 || !isJsonResponse(response)) {
    return false;
  }

  try {
    const payload = await response.clone().json();
    const message = [payload?.message, payload?.error]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return message.includes("csrf");
  } catch {
    return false;
  }
};

export const fetchCsrfToken = async () => {
  if (fetchingPromise) return fetchingPromise;

  fetchingPromise = (async () => {
    try {
      const response = await fetch(SummaryApi.getCsrfToken.url, {
        method: SummaryApi.getCsrfToken.method,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`CSRF token request failed with ${response.status}`);
      }

      const data = await response.json();

      if (!data?.csrfToken) {
        throw new Error("CSRF token missing from response");
      }

      csrfToken = data.csrfToken;
      fetchingPromise = null;
      return csrfToken;
    } catch (err) {
      console.error("Failed to fetch CSRF token", err);
      fetchingPromise = null;
      throw err;
    }
  })();

  return fetchingPromise;
};

export const request = async (apiConfig, options = {}) => {
  const normalizedConfig =
    typeof apiConfig === "string"
      ? {
          url: apiConfig,
          method: (options.method || "get").toLowerCase(),
        }
      : apiConfig;

  if (!normalizedConfig?.url) {
    throw new Error("request() requires a URL or api config object with url");
  }

  const method = (normalizedConfig.method || "get").toLowerCase();
  const isPostLike = !["get", "head", "options"].includes(method);

  if (!csrfToken && isPostLike) {
    await fetchCsrfToken();
  }

  // Support legacy callers passing payload as apiConfig.data or options.data
  const requestOptions = { ...options };
  delete requestOptions.method;

  if (requestOptions.body === undefined) {
    if (normalizedConfig.data !== undefined) {
      requestOptions.body = JSON.stringify(normalizedConfig.data);
    } else if (requestOptions.data !== undefined) {
      requestOptions.body = JSON.stringify(requestOptions.data);
    }
  }
  delete requestOptions.data;

  const headers = {
    ...requestOptions.headers,
  };

  if (
    requestOptions.body !== undefined &&
    !(requestOptions.body instanceof FormData)
  ) {
    headers["Content-Type"] = "application/json";
  }

  if (isPostLike) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  const response = await fetch(normalizedConfig.url, {
    ...requestOptions,
    method,
    headers,
    credentials: "include",
  });

  if (isPostLike && (await isCsrfFailureResponse(response))) {
    // Token might have expired or been cleared, force fetch a new one
    csrfToken = null;
    await fetchCsrfToken();
    headers["X-CSRF-Token"] = csrfToken;
    return fetch(normalizedConfig.url, {
      ...requestOptions,
      method,
      headers,
      credentials: "include",
    });
  }

  return response;
};
